import { useState } from 'react'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { useTranslation } from '../../contexts/LanguageContext'
import type { Translations } from '../../i18n/types'
import type { CVReviewResponse, ReviewItem, ReviewSection } from '../../types/api'

interface CVReviewModalProps {
  isOpen: boolean
  onClose: () => void
  review: CVReviewResponse
  onApplyFix?: (section: ReviewSection, field: string | undefined, fix: string, itemId?: string) => Promise<boolean> | boolean
}

function getSectionLabels(t: Translations): Record<ReviewSection, string> {
  return {
    personal: t.aiReview.sectionPersonal,
    summary: t.aiReview.sectionSummary,
    experience: t.aiReview.sectionExperience,
    education: t.aiReview.sectionEducation,
    skills: t.aiReview.sectionSkills,
    languages: t.aiReview.sectionLanguages,
    general: t.aiReview.sectionGeneral,
  }
}

function getScoreColor(score: number): string {
  if (score >= 80) return 'text-green-600'
  if (score >= 60) return 'text-yellow-600'
  return 'text-red-600'
}

function getScoreLabel(score: number, t: Translations): string {
  if (score >= 80) return t.aiReview.excellent
  if (score >= 60) return t.aiReview.good
  if (score >= 40) return t.aiReview.needsImprovement
  return t.aiReview.needsAttention
}

function getScoreBarColor(score: number): string {
  if (score >= 80) return 'bg-green-500'
  if (score >= 60) return 'bg-yellow-500'
  return 'bg-red-500'
}

function getItemIcon(type: ReviewItem['type']): JSX.Element {
  switch (type) {
    case 'error':
      return (
        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center">
          <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </span>
      )
    case 'warning':
      return (
        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-yellow-100 flex items-center justify-center">
          <svg className="w-3 h-3 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </span>
      )
    case 'suggestion':
      return (
        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
          <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        </span>
      )
    case 'success':
      return (
        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
          <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </span>
      )
  }
}

function getItemBorderColor(type: ReviewItem['type']): string {
  switch (type) {
    case 'error':
      return 'border-l-red-500'
    case 'warning':
      return 'border-l-yellow-500'
    case 'suggestion':
      return 'border-l-blue-500'
    case 'success':
      return 'border-l-green-500'
  }
}

export function CVReviewModal({
  isOpen,
  onClose,
  review,
  onApplyFix,
}: CVReviewModalProps) {
  const { t } = useTranslation()
  const { score, items, strengths, improvements } = review
  const [resolvedItems, setResolvedItems] = useState<Map<number, 'applied' | 'copied'>>(new Map())
  const sectionLabels = getSectionLabels(t)

  // Sort items: errors first, then warnings, then suggestions, then success
  const sortedItems = [...items].sort((a, b) => {
    const order = { error: 0, warning: 1, suggestion: 2, success: 3 }
    return order[a.type] - order[b.type]
  })

  const handleApplyFix = async (index: number, section: ReviewSection, field: string | undefined, fix: string, itemId?: string) => {
    if (onApplyFix) {
      const wasDirectlyApplied = await onApplyFix(section, field, fix, itemId)
      setResolvedItems(prev => {
        const newMap = new Map(prev)
        newMap.set(index, wasDirectlyApplied ? 'applied' : 'copied')
        return newMap
      })
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t.aiReview.reviewTitle} size="lg">
      <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2 -mr-2">
        {/* Score Section */}
        <div className="bg-slate-50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-600">{t.aiReview.scoreLabel}</span>
            <span className={`text-2xl font-bold ${getScoreColor(score)}`}>
              {score}/100
            </span>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div
              className={`h-full ${getScoreBarColor(score)} transition-all duration-500`}
              style={{ width: `${score}%` }}
            />
          </div>
          <p className={`text-sm mt-1 ${getScoreColor(score)}`}>
            {getScoreLabel(score, t)}
          </p>
        </div>

        {/* Strengths */}
        {strengths.length > 0 && (
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-2">
              <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {t.aiReview.strengths}
            </h3>
            <ul className="space-y-1">
              {strengths.map((strength, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                  <span className="text-green-500 mt-0.5">•</span>
                  {strength}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Improvements */}
        {improvements.length > 0 && (
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-2">
              <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
              </svg>
              {t.aiReview.improvements}
            </h3>
            <ul className="space-y-1">
              {improvements.map((improvement, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                  <span className="text-yellow-500 mt-0.5">•</span>
                  {improvement}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Detailed Items */}
        {sortedItems.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-slate-800 mb-3">
              {t.aiReview.detailedRecommendations}
            </h3>
            <div className="space-y-3">
              {sortedItems.map((item, i) => (
                <div
                  key={i}
                  className={`bg-white border border-slate-200 border-l-4 ${getItemBorderColor(item.type)} rounded-lg p-3`}
                >
                  <div className="flex items-start gap-2">
                    {getItemIcon(item.type)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                          {sectionLabels[item.section]}
                        </span>
                        <span className="text-sm font-medium text-slate-800">
                          {item.title}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">
                        {item.message}
                      </p>
                      {item.fix && (
                        <div className={`mt-2 rounded-lg p-3 border transition-colors ${
                          resolvedItems.has(i)
                            ? resolvedItems.get(i) === 'applied'
                              ? 'bg-green-50 border-green-200'
                              : 'bg-blue-50 border-blue-200'
                            : 'bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200'
                        }`}>
                          <p className="text-xs font-medium text-slate-500 mb-1">{t.aiReview.suggestion}</p>
                          <p className={`text-sm italic leading-relaxed ${
                            resolvedItems.has(i)
                              ? resolvedItems.get(i) === 'applied'
                                ? 'text-green-700'
                                : 'text-blue-700'
                              : 'text-slate-700'
                          }`}>
                            "{item.fix}"
                          </p>
                          {onApplyFix && (
                            resolvedItems.has(i) ? (
                              resolvedItems.get(i) === 'applied' ? (
                                <span className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-100 rounded-lg">
                                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                  {t.aiReview.applied}
                                </span>
                              ) : (
                                <span className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-100 rounded-lg">
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                                  </svg>
                                  {t.aiReview.copiedPaste}
                                </span>
                              )
                            ) : (
                              <button
                                onClick={() => handleApplyFix(i, item.section, item.field, item.fix!, item.itemId)}
                                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-primary to-[#FF8E53] rounded-lg shadow-sm hover:shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                {t.aiReview.applyFix}
                              </button>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-6 pt-4 border-t border-slate-200 flex justify-end">
        <Button variant="secondary" onClick={onClose}>
          {t.aiReview.close}
        </Button>
      </div>
    </Modal>
  )
}
